<template>
  <el-dropdown
    data-testid="project-chooser-dropdown"
    :disabled="pending"
    placement="bottom-end"
    flyweight
    @command="handleCommand"
    @visible-change="visibleChange"
  >
    <el-button
      icon="i-chevron-down--16"
      right-icon
      type="ghost"
    >
      {{ currentProjectLabel || $t('Choose a project') }}
    </el-button>
    <template #dropdown>
      <el-dropdown-menu
        class="project-chooser"
        :no-data-message="$t('No projects found')"
      >
        <el-input
          v-model="search"
          autofocus
          class="search-input"
          :placeholder="$t('Search')"
          size="small"
          suffix-icon="i-search-o--16"
        />
        <el-dropdown-item
          v-if="isDomainAdmin"
          class="domain-dropdown-item"
          :class="{ 'is-selected': domainMode }"
          :command="{ target: domain, action: 'domain' }"
          icon="i-root-o--16"
          data-testid="domain-administration"
        >
          <div class="pl-8">
            <div class="name">
              {{ domain }}
            </div>
            <div class="description">
              {{ $t('User and project management') }}
            </div>
          </div>
        </el-dropdown-item>
        <el-dropdown-item
          v-for="(projectItem, index) in projectsFiltered"
          :key="projectItem.value"
          :class="{ disabled: isDisabled(projectItem), 'is-selected': projectItem.value === project }"
          :command="{ target: projectItem.value, action: 'project' }"
          :data-testid="projectItem.label"
          :disabled="isDisabled(projectItem)"
          icon="i-case-o--16"
          :divided="isDomainAdmin && index === 0"
        >
          <span class="left">
            <span class="label pl-8">{{ projectItem.label }}</span>
          </span>
          <span
            v-if="!projectItem.enabled"
            class="right"
          >
            <span class="label">{{ $t('Disabled') }}</span>
          </span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script>
  import escapeRegexp from '@/common/utils/escapeRegexp';
  import {
    DEFAULT_DOMAIN_NAME
  } from '@/common/constants/domains.constants';
  import useAuthStore from '@/common/store/pinia/auth';
  import { mapState, mapStores } from 'pinia';

  export default {
    name: 'ProjectChooser',
    data() {
      return {
        projectChooserDomainProjectsViewName: 'projectChooserDomainProjects',
        pending: false,
        search: ''
      };
    },
    computed: {
      ...mapStores(useAuthStore),
      ...mapState(useAuthStore, {
        domain: 'domain',
        project: 'currentProject',
        domainMode: 'domainMode',
        isDomainAdmin: 'isDomainAdmin'
      }),
      currentProjectLabel() {
        return (this.project && this.projects.find(project => project.value === this.project)?.label)
          || (this.domainMode && this.domain);
      },
      projects() {
        const projects = this.$store.getters['projects/getItems'];
        const sortedProjects = [...projects].sort((a, b) => a?.name?.localeCompare(b?.name));

        if (this.isDomainAdmin && this.domainProjects.length > 0) {
          return sortedProjects
            .map(project => {
              const domainProject = this.$store.getters['domains/projects/getItemById'](project.id);
              return { value: project.id, label: domainProject.name, enabled: domainProject.isEnabled };
            });
        }

        return sortedProjects
          .map(project => ({ value: project.id, label: project.name, enabled: project.enabled }));
      },
      projectsFiltered() {
        const searchString = this.search?.trim() || '';
        if (!searchString) {
          return this.projects;
        }

        const regexp = RegExp(escapeRegexp(searchString), 'i');
        return this.projects.filter(project => regexp.test(project.label));
      },
      domainProjects() {
        return this.$store.getters['domains/projects/getItemsByView'](this.projectChooserDomainProjectsViewName) || [];
      }
    },
    watch: {
      domainMode: {
        immediate: true,
        handler(newVal, oldVal) {
          if (newVal && !oldVal) {
            this.fetchDomainProjects();
          }
        }
      }
    },
    methods: {
      handleCommand({ target, action }) {
        const promise = action === 'domain'
          ? this.authStore.switchToDomainMode()
          : this.authStore.signInProject(target);

        this.pending = true;
        promise
          .finally(() => {
            this.pending = false;
          });
      },
      visibleChange() {
        this.search = '';
      },
      isDisabled(item) {
        return !item.enabled || !item.value || this.project === item.value;
      },
      fetchDomainProjects() {
        const projectsCount = this.projects.length > 0 ? this.projects.length : undefined;

        // +1 goes because of the 'service' project that appears for default domain and
        // because of that we may get one project less in the list which causes mapping error
        const limit = this.domain === DEFAULT_DOMAIN_NAME && projectsCount !== undefined
          ? projectsCount + 1
          : projectsCount;

        return this.$store.$takeLast('domains/projects/FETCH_ITEMS', {
          limit,
          view: this.projectChooserDomainProjectsViewName
        }, 300, false);
      }
    }
  };
</script>

<style scoped lang="scss">
  .is-selected {
    background-color: $av-brand-accent;
  }

  .disabled {
    pointer-events: none;
    display: flex;
    align-items: center;
    width: 100%;

    .left {
      min-width: 0;
      flex: 1 1 auto;
      text-overflow: ellipsis;
      overflow-x: hidden;
      max-width: 180px;
    }

    .right {
      flex: 0 0 auto;
      text-align: right;
      padding-left: 8px;
    }

    .i {
      color: $av-brand-light;
    }

    .label {
      color: $av-fixed-light;
    }
  }

  .search-input ::v-deep .el-input__container {
    height: auto;
    margin-top: -4px;
    padding: 4px 8px 8px;
    border-radius: 0;
    border: none;
    border-bottom: 1px solid $av-brand-light;
  }

  .domain-dropdown-item {
    padding: 8px 16px;
    margin-top: 8px;
    line-height: 24px;

    .description {
      color: $av-fixed-light;
      font-weight: 400;
    }
  }
</style>
